import {Button, Card, CircularProgress, createStyles, Grid, makeStyles, TextField} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {Fragment, useState} from 'react';
import {useParams} from 'react-router-dom';
import API from '../../APIs/API';
import {User} from '../../Auth/User';
import Utils from '../../Core/Utils';
import {SSNTable} from './SSNTable';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';

const useStyles = makeStyles(() =>
    createStyles({
        formControl: {
            marginTop: 8,
            width: '100%',
        },
    }),
);
export type SSNData = {name: string; value: string};

export const SSNPanel = observer(
    ({parameterStore, fullWidth = false}: {parameterStore: ParameterStore | null; fullWidth?: boolean}) => {
        const classes = useStyles();
        const [inp, setInp] = useState([
            {n: 'firstName', v: 'Lucy'},
            {n: 'lastName', v: 'Williams'},
            {n: 'dob', v: '1948-04-08'},
            {n: 'socialSecurityNumber', v: '222222222'},
            {n: 'address', v: '32 Ivy Hill Dr'},
            {n: 'addressCity', v: 'Smithtown'},
            {n: 'state', v: 'NY'},
            {n: 'addressZipCode', v: '11787'},
        ]);
        const {historyFormId} = useParams<never>();

        const getParam = (name: string): any => {
            if (parameterStore?.approval == null) {
                return inp.find((x) => x.n == name)?.v;
            } else {
                if (name == 'dob') {
                    return (
                        parameterStore.approval.byear?.value +
                        '-' +
                        parameterStore.approval.bmonth?.value +
                        '-' +
                        parameterStore.approval.bday?.value
                    );
                } else {
                    return parameterStore.approval[name as keyof typeof parameterStore.approval]?.value;
                }
            }
        };
        const setParam = (name: string, v: any) => {
            if (parameterStore?.approval == null) {
                const p = inp.find((x) => x.n == name);
                if (p != null) {
                    p.v = v;
                }
                setInp(Utils.jsClone(inp));
            } else {
                runInAction(() => {
                    const p = parameterStore.approval[name as keyof typeof parameterStore.approval];
                    if (p != null) {
                        p.value = v;
                    }
                });
            }
        };

        const [loading, setLoading] = useState(null as null | boolean);
        const [data, setData] = useState(parameterStore?.ssnData || Array<SSNData>());
        const [err, setErr] = useState('');

        const GetRecord = async () => {
            setErr('');
            setLoading(true);

            const args = {
                firstname: getParam('firstName'),
                lastname: getParam('lastName'),
                street: getParam('address'),
                city: getParam('addressCity'),
                state: getParam('state'),
                dob: getParam('dob'),
                zip: getParam('addressZipCode'),
                ssn: getParam('socialSecurityNumber'),
            };

            const resp = await API.Call<{
                error: string | null;
                PlatformResponse: {
                    Response: {
                        [key: string]: ({_: string} & {
                            [key: string]: {[key: string]: string & {[key: string]: string}}[];
                        })[];
                    }[];
                    TransactionDetails: {Errors: string[] & {Error: {$: {code: string; message: string}}[]}[]}[];
                };
            }>('identity', 'POST', args);

            API.LogToMixPanel('agent-request-ssn', {
                firstname: getParam('firstName'),
                lastname: getParam('lastName'),
                state: getParam('state'),
                dob: getParam('dob'),
                agent: User.Info.email,
            });

            const error = resp?.PlatformResponse.TransactionDetails[0].Errors[0];

            if ((typeof error === 'string' && error.length === 0) || error?.Error == null) {
                const out = new Array<{name: string; value: string}>();
                out.push({
                    name: 'Date Of Birth',
                    value: resp?.PlatformResponse?.Response[0]?.DateOfBirthResult[0]?._ || '',
                });
                out.push({
                    name: 'SSN',
                    value: resp?.PlatformResponse?.Response[0]?.SocialSecurityNumberResult[0]?._ || '',
                });
                out.push({
                    name: 'Deceased',
                    value: resp?.PlatformResponse?.Response[0]?.SsnValidation[0]?.DeceasedResult[0]?._ || '',
                });
                out.push({
                    name: 'SSN',
                    value: resp?.PlatformResponse?.Response[0]?.SsnValidation[0]?.FormatResult[0]?._ || '',
                });
                out.push({
                    name: 'SSN Issued',
                    value: resp?.PlatformResponse?.Response[0]?.SsnValidation[0]?.IssueResult[0]?._ || '',
                });
                out.push({name: 'Fraud', value: resp?.PlatformResponse?.Response[0]?.ReportedFraudResult[0]?._ || ''});
                out.push({name: 'Minor', value: resp?.PlatformResponse?.Response[0]?.MinorResult[0]?._ || ''});
                out.push({
                    name: 'Address',
                    value: resp?.PlatformResponse?.Response[0]?.AddressHighRiskResult[0]?._ || '',
                });
                out.push({
                    name: 'Address Type',
                    value: resp?.PlatformResponse?.Response[0]?.AddressTypeResult[0]?._ || '',
                });
                out.push({
                    name: 'Address Verification',
                    value: resp?.PlatformResponse?.Response[0]?.AddressVerificationResult[0]?._ || '',
                });

                if (resp?.PlatformResponse?.Response[0]?.AdditionalAddresses[0]?.AdditionalAddress != null) {
                    resp.PlatformResponse.Response[0].AdditionalAddresses[0].AdditionalAddress.forEach(
                        (x: any, i: any) => {
                            out.push({
                                name: 'Address ' + (i + 1),
                                value:
                                    x.Street[0] +
                                    ' ' +
                                    x.City[0] +
                                    ' ' +
                                    x.State[0] +
                                    ' ' +
                                    x.ZipCode[0] +
                                    ' [Reported ' +
                                    (x.ReportDate?.[0].Year?.[0] || '') +
                                    '] [Updated ' +
                                    (x.UpdateDate?.[0].Year?.[0] || '') +
                                    ']',
                            });
                        },
                    );
                }
                const FraudShieldResults = resp?.PlatformResponse.Response[0].FraudShieldResults;
                if (FraudShieldResults && FraudShieldResults[0]) {
                    const fraudShields = Object.keys(FraudShieldResults[0]);
                    fraudShields.forEach((key) => {
                        if (FraudShieldResults[0][key][0].$.code === 'Y') {
                            out.push({name: key, value: FraudShieldResults[0][key][0]._});
                        }
                    });
                }
                if (parameterStore) {
                    runInAction(() => {
                        parameterStore.ssnData = out;
                    });
                }

                setData(out);
            } else if (resp?.error !== null) {
                console.log('error', error.Error[0].$.code, error.Error[0].$.message);
                setErr(error.Error[0].$.code + ':' + error.Error[0].$.message);
                setData(new Array<{name: string; value: string}>());
            }

            setLoading(false);
        };

        return (
            <Grid container spacing={3}>
                <Grid item xs={fullWidth ? 12 : 9}>
                    <Card style={{padding: 20}}>
                        <h4>SSN Verification</h4>
                        <Grid container spacing={3}>
                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="First Name"
                                    value={getParam('firstName')}
                                    onChange={(e) => {
                                        setParam('firstName', e.target.value);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="Last Name"
                                    value={getParam('lastName')}
                                    onChange={(e) => {
                                        setParam('lastName', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="SSN (9 digits, no special chars)"
                                    value={getParam('socialSecurityNumber')}
                                    onChange={(e) => {
                                        setParam('socialSecurityNumber', e.target.value);
                                    }}
                                    inputProps={{
                                        autoComplete: 'new-password',
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="Birth Date (YYYY-MM-DD)"
                                    value={getParam('dob')}
                                    onChange={(e) => {
                                        setParam('dob', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="Street Address"
                                    value={getParam('address')}
                                    onChange={(e) => {
                                        setParam('address', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="City"
                                    value={getParam('addressCity')}
                                    onChange={(e) => {
                                        setParam('addressCity', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="State (2 letter abbreviation)"
                                    value={getParam('state')}
                                    onChange={(e) => {
                                        setParam('state', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    className={classes.formControl}
                                    variant="outlined"
                                    label="Zip Code (5 Digit)"
                                    value={getParam('addressZipCode')}
                                    onChange={(e) => {
                                        setParam('addressZipCode', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} style={{textAlign: 'right'}}>
                                {loading ? (
                                    <Fragment>
                                        <div style={{marginRight: 30}}>
                                            <span>loading... this may take upto 60 seconds...</span>
                                            <CircularProgress />
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Button
                                        variant="contained"
                                        disabled={historyFormId}
                                        color="primary"
                                        onClick={GetRecord}
                                    >
                                        Validate
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                <Grid item xs={fullWidth ? 12 : 9}>
                    {loading === false || data?.length ? (
                        <Card style={{padding: 20}}>
                            {err != '' ? <Alert severity="warning">{err}</Alert> : <SSNTable data={data} />}
                        </Card>
                    ) : null}
                </Grid>
            </Grid>
        );
    },
);

export default SSNPanel;
